import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { AppConfig } from '../config/app-config';
import { AnalyticsEvents, INSTALL_GATE_PAGE_TITLE } from '../analytics/analytics.constants';
import { AnalyticsService } from '../analytics/analytics.service';
import { CmsBackgroundComponent } from './cms-background.component';
import { PwaInstallService } from '../services/pwa-install.service';
import { detectInstallPlatform } from '../utils/install-instructions';
import { resolveInstallScreenState } from '../utils/install-screen-state';
import { STARTUP_HELPER_URL, isStartupHelperBrowser } from '../utils/startup-helper';

/**
 * How long to wait after "Launch app" before deciding the launch didn't take —
 * if the browser still holds focus (the OS never switched to the installed app
 * window), the app is likely gone, so we reveal the "install again" escape.
 * Long enough not to false-flag a normal launch (which moves focus to the app
 * within a moment) or a quick permission-prompt confirm.
 */
const LAUNCH_OUTCOME_MS = 2500;

/**
 * Dead-end screen shown when the `environment.blockBrowserAccess` kill-switch
 * is on and the session is running in a browser rather than the installed app
 * (see core/utils/browser-access-gate.ts). There is deliberately no dismiss and
 * no "continue in browser" — installing, or launching the installed app, is the
 * only way forward.
 *
 * It offers exactly one action, chosen from what the platform can actually tell
 * us (see resolveInstallScreenState + PwaInstallService):
 *   - installed  → "Launch app" (deep link to the installed PWA).
 *   - installable→ "Install JioPC" (Chromium's beforeinstallprompt).
 *   - detecting  → a brief spinner, so the wrong button never flashes.
 *   - manual     → platform-specific "add to home screen" steps (iOS/Firefox,
 *                  where install can neither be prompted nor detected).
 * When the user installs from here, `appinstalled` flips the state to
 * "installed" automatically, so the screen never traps them in an install→
 * install loop.
 *
 * Install-gate analytics on {@link INSTALL_GATE_PAGE_TITLE} (`website_page`):
 * one `page_load` when shown, plus `install_now` / `launch_now` on the CTAs.
 * Production blocks the browser before signup, so this is the visit from
 * someone who opened the JioPC URL and never started registration.
 * `device_id` is attached by AnalyticsService; `platform_metadata.platform`
 * stays `browser`.
 *
 * Layout follows the Figma: logo + title + subtitle in one group (24px gaps),
 * the CTA group 64px below. Type is JioType (the app's default font).
 */
@Component({
  selector: 'app-install-required',
  standalone: true,
  imports: [CmsBackgroundComponent],
  template: `
    <app-cms-background fallback="login" containerClass="flex items-center justify-center px-6 py-12">
      @if (state() === 'detecting') {
        <div class="h-10 w-10 animate-spin rounded-full border-4 border-brand-purple border-t-transparent" role="status" aria-label="Checking install status"></div>
      } @else {
        <div class="flex w-full max-w-3xl flex-col items-center gap-16 text-center">
          <!-- Group 1: logo + title + subtitle (24px gaps). -->
          <div class="flex flex-col items-center gap-6">
            <img src="/jio-pc-icon.svg" [alt]="appName" width="64" height="64" class="h-16 w-16" />

            @if (state() === 'installed') {
              <h1 class="text-[40px] font-black leading-[40px] tracking-[-0.03em] text-white">{{ appName }} is installed</h1>
              <p class="whitespace-nowrap text-[24px] font-medium leading-[32px] tracking-[-0.005em] text-white/[0.78]">
                Launch the app to continue
              </p>
            } @else {
              <h1 class="text-[40px] font-black leading-[40px] tracking-[-0.03em] text-white">Add {{ appName }} to your desktop</h1>
              <p class="max-w-xl text-[24px] font-medium leading-[32px] tracking-[-0.005em] text-white/[0.78]">
                Start your {{ appName }} anytime with one click from your desktop - whenever you
                need it
              </p>
            }
          </div>

          <!-- Group 2: the single call-to-action, 64px below group 1. -->
          <div class="flex w-full flex-col items-center">
            @if (state() === 'installed') {
              @if (opening() && !launchFailed()) {
                <p class="max-w-xl text-[15px] leading-relaxed text-white/70">
                  Launching {{ appName }}… if nothing happens, open it from your applications, then close this tab.
                </p>
              } @else {
                <button
                  type="button"
                  (click)="openApp()"
                  class="inline-flex items-center justify-center rounded-full border border-transparent bg-purple-accent px-7 text-white transition-colors hover:bg-purple-accent/85 focus-visible:outline-none h-12 text-button"
                >
                  Launch app
                </button>

                <!-- Escape hatch — shown only after a Launch attempt that never
                     switched to the app (e.g. macOS "Move to Bin", which doesn't
                     fire the browser's uninstall signals): let the user reset to
                     the Install flow instead of looping on a dead "Launch app". -->
                @if (launchFailed()) {
                  <!-- The "launch never switched to the app" state covers two cases: a
                       genuine uninstall (e.g. macOS "Move to Bin", which doesn't fire the
                       browser's uninstall signals) AND an OS-level launch block (Windows
                       refusing to run Chrome's chrome_proxy.exe, or AV/EDR/AppLocker
                       quarantining it). Reinstall is the primary self-serve fix and stays
                       first; it can't clear an OS block, so the secondary note below speaks
                       only to users who actually saw a permission/security error. -->
                  <button
                    type="button"
                    (click)="reinstall()"
                    class="mt-4 cursor-pointer text-sm text-white/60 underline underline-offset-4 transition-colors hover:text-white/90"
                  >
                    App removed or not opening? Install it again
                  </button>
                  <p class="mt-3 max-w-xl text-xs leading-relaxed text-white/50">
                    Saw a permission or security error when launching? {{ appName }} may be
                    blocked by your device's security software — please contact your IT administrator.
                  </p>
                }
              }
            } @else if (state() === 'installable') {
              <button
                type="button"
                (click)="install()"
                class="inline-flex items-center justify-center rounded-full border border-transparent bg-purple-accent px-7 text-white transition-colors hover:bg-purple-accent/85 focus-visible:outline-none h-12 text-button"
              >
                Install JioPC
              </button>
            } @else {
              <div class="w-full max-w-xl rounded-2xl border border-border-overlay bg-surface/70 p-5 text-left backdrop-blur-sm">
                <p class="mb-2 text-sm font-semibold text-white">
                  @switch (platform) {
                    @case ('firefox') { Firefox can't run {{ appName }} as an app }
                    @default { To use {{ appName }}, install it first }
                  }
                </p>
                <p class="text-sm leading-relaxed text-white/80">
                  @switch (platform) {
                    @case ('ios') {
                      In Safari, tap the <span class="font-semibold text-white">Share</span> icon and choose
                      <span class="font-semibold text-white">Add to Home Screen</span>. Then open {{ appName }} from
                      your <span class="font-semibold text-white">Home Screen</span> — it can't be opened from the browser.
                    }
                    @case ('safari') {
                      In Safari, open the <span class="font-semibold text-white">Share</span> menu (or
                      <span class="font-semibold text-white">File</span>) and choose
                      <span class="font-semibold text-white">Add to Dock</span>. Then open {{ appName }} from your
                      <span class="font-semibold text-white">Dock</span> or Applications — it can't be opened from the browser.
                    }
                    @case ('firefox') {
                      Firefox can't install or open {{ appName }}. Open this page in
                      <span class="font-semibold text-white">Chrome</span> or
                      <span class="font-semibold text-white">Edge</span> to install it. If you've already added it,
                      launch {{ appName }} from your device instead of the browser.
                    }
                    @case ('android') {
                      Open the browser menu and tap
                      <span class="font-semibold text-white">Install JioPC</span> (or
                      <span class="font-semibold text-white">Add to Home screen</span>). Then open {{ appName }} from
                      your home screen.
                    }
                    @case ('desktop') {
                      Click the <span class="font-semibold text-white">install</span> icon in your browser's address
                      bar, then confirm. {{ appName }} will open in its own window.
                    }
                    @default {
                      Open this page in <span class="font-semibold text-white">Chrome</span> or
                      <span class="font-semibold text-white">Edge</span> to install {{ appName }}. If you've already
                      added it, launch it from your device instead of the browser.
                    }
                  }
                </p>
              </div>
            }

            <!-- Windows sign-in launcher: a plain link the user clicks, never a scripted
                 download — corporate proxies (Zscaler) hold or block an .exe fetched
                 without a user gesture. Run it before or after installing: it waits for
                 the PWA install, then confirms (see tools/startup-launcher). -->
            @if (showLauncherLink) {
              <a
                [href]="launcherUrl"
                download
                class="mt-8 text-sm font-medium text-white/80 underline underline-offset-4 transition-colors hover:text-white"
              >
                Download JioPC Launcher to open {{ appName }} automatically when Windows starts
              </a>
            }
          </div>
        </div>
      }
    </app-cms-background>
  `,
})
export class InstallRequiredComponent implements OnDestroy {
  private readonly pwaInstall = inject(PwaInstallService);
  private readonly analytics = inject(AnalyticsService);

  constructor() {
    this.analytics.log(AnalyticsEvents.PAGE_LOAD, INSTALL_GATE_PAGE_TITLE);
  }

  readonly appName = AppConfig.JIO_CLOUD_PC_APP_NAME;
  readonly platform = detectInstallPlatform();
  /** Windows Chrome/Edge only — where the launcher can find the installed app. */
  readonly showLauncherLink = isStartupHelperBrowser();
  readonly launcherUrl = STARTUP_HELPER_URL;
  readonly opening = signal(false);
  /** True only after a Launch attempt that never switched to the app — reveals the "install again" escape. */
  readonly launchFailed = signal(false);

  /** Tears down the pending launch-outcome watch (visibility listener + timer). */
  private launchCleanup?: () => void;

  /** The single action to present, recomputed as install signals resolve. */
  readonly state = computed(() =>
    resolveInstallScreenState({
      installed: this.pwaInstall.installed(),
      canInstall: this.pwaInstall.canInstall(),
      settled: this.pwaInstall.settled(),
    }),
  );

  openApp(): void {
    this.analytics.log(AnalyticsEvents.LAUNCH_NOW, INSTALL_GATE_PAGE_TITLE);
    this.pwaInstall.openApp();
    // Show a transient "Launching…" hint, then judge the outcome by tab
    // visibility: a successful launch backgrounds this tab (the app window takes
    // focus); if it never does, the app is likely gone — reveal the escape.
    this.opening.set(true);
    this.launchFailed.set(false);
    this.armLaunchOutcome();
  }

  install(): void {
    this.analytics.log(AnalyticsEvents.INSTALL_NOW, INSTALL_GATE_PAGE_TITLE);
    // On success, `appinstalled` flips state → 'installed', swapping this for "Launch app".
    void this.pwaInstall.promptInstall();
  }

  reinstall(): void {
    // The user confirms the app is gone but the browser hasn't detected it (e.g.
    // macOS "Move to Bin"). Forget the installed state so `state()` recomputes to
    // the Install flow.
    this.resetLaunch();
    this.pwaInstall.markUninstalled();
  }

  ngOnDestroy(): void {
    this.launchCleanup?.();
  }

  /**
   * After a Launch attempt, judge the outcome by browser FOCUS (not tab
   * visibility): a successful launch moves OS focus to the installed app window
   * — but on macOS this browser tab often stays `visible` behind it, so
   * `visibilitychange` is unreliable while `blur` / `document.hasFocus()` are not.
   *  - the browser loses focus → the app opened; when the user returns
   *    (`focus`), restore the "Launch app" CTA (no escape shown — launch worked).
   *  - the browser still holds focus after the grace window → the deep link
   *    opened nothing → mark the launch failed so the "install again" escape appears.
   */
  private armLaunchOutcome(): void {
    this.launchCleanup?.();

    let leftBrowser = false;
    const onBlur = (): void => {
      leftBrowser = true; // focus moved out of the browser → the app window opened
    };
    const onFocus = (): void => {
      if (leftBrowser) this.resetLaunch(); // back after a successful launch
    };

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    const timer = setTimeout(() => {
      if (!leftBrowser && document.hasFocus()) this.launchFailed.set(true);
    }, LAUNCH_OUTCOME_MS);

    this.launchCleanup = () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      clearTimeout(timer);
      this.launchCleanup = undefined;
    };
  }

  private resetLaunch(): void {
    this.launchCleanup?.();
    this.opening.set(false);
    this.launchFailed.set(false);
  }
}
